// Browser compatibility module
// Adds polyfills and patches for older browsers

export function initialize() {
  console.log("Initializing browser compatibility fixes");
  
  // Feature detection for ES6 arrow functions
  const supportsArrowFunctions = checkArrowFunctionSupport();
  
  if (!supportsArrowFunctions) {
    console.log("Browser doesn't support arrow functions - adding warning to console");
    // We can't actually polyfill syntax, but we can warn developers
    console.warn("Your browser doesn't support ES6 arrow functions. Some features may not work correctly.");
  }
  
  // Add Promise polyfill for older browsers if needed
  if (typeof Promise === 'undefined') {
    console.log("Promise not supported - adding polyfill");
    installPromisePolyfill();
  }
  
  return true;
}

// Check if browser supports arrow functions
function checkArrowFunctionSupport() {
  try {
    // Try to create a function using arrow function syntax
    // This will throw a SyntaxError in browsers that don't support it
    
    // Use Function constructor to avoid syntax errors at parse time in old browsers
    // We wrap the arrow function in quotes to make it a string
    new Function("return (x) => x + 1");
    return true;
  } catch (error) {
    return false;
  }
}

// Simple Promise polyfill
function installPromisePolyfill() {
  if (typeof Promise !== 'undefined') return; // Already exists
  
  // Extremely basic Promise implementation for compatibility
  // This is not a complete polyfill but handles basic then/catch
  window.Promise = function(executor) {
    var self = this;
    self.status = 'pending';
    self.value = null;
    self.reason = null;
    self.onFulfilledCallbacks = [];
    self.onRejectedCallbacks = [];
    
    function resolve(value) {
      if (self.status === 'pending') {
        self.status = 'fulfilled';
        self.value = value;
        self.onFulfilledCallbacks.forEach(function(callback) {
          callback(value);
        });
      }
    }
    
    function reject(reason) {
      if (self.status === 'pending') {
        self.status = 'rejected';
        self.reason = reason;
        self.onRejectedCallbacks.forEach(function(callback) {
          callback(reason);
        });
      }
    }
    
    try {
      executor(resolve, reject);
    } catch (error) {
      reject(error);
    }
  };
  
  window.Promise.prototype.then = function(onFulfilled, onRejected) {
    var self = this;
    var promise2 = new window.Promise(function(resolve, reject) {
      if (self.status === 'fulfilled') {
        setTimeout(function() {
          try {
            if (typeof onFulfilled === 'function') {
              var x = onFulfilled(self.value);
              resolve(x);
            } else {
              resolve(self.value);
            }
          } catch (error) {
            reject(error);
          }
        }, 0);
      }
      
      if (self.status === 'rejected') {
        setTimeout(function() {
          try {
            if (typeof onRejected === 'function') {
              var x = onRejected(self.reason);
              resolve(x);
            } else {
              reject(self.reason);
            }
          } catch (error) {
            reject(error);
          }
        }, 0);
      }
      
      if (self.status === 'pending') {
        self.onFulfilledCallbacks.push(function(value) {
          setTimeout(function() {
            try {
              if (typeof onFulfilled === 'function') {
                var x = onFulfilled(value);
                resolve(x);
              } else {
                resolve(value);
              }
            } catch (error) {
              reject(error);
            }
          }, 0);
        });
        
        self.onRejectedCallbacks.push(function(reason) {
          setTimeout(function() {
            try {
              if (typeof onRejected === 'function') {
                var x = onRejected(reason);
                resolve(x);
              } else {
                reject(reason);
              }
            } catch (error) {
              reject(error);
            }
          }, 0);
        });
      }
    });
    
    return promise2;
  };
  
  window.Promise.prototype.catch = function(onRejected) {
    return this.then(null, onRejected);
  };
  
  window.Promise.resolve = function(value) {
    return new window.Promise(function(resolve) {
      resolve(value);
    });
  };
  
  window.Promise.reject = function(reason) {
    return new window.Promise(function(resolve, reject) {
      reject(reason);
    });
  };
  
  console.log("Promise polyfill installed");
}

// Initialize when module is imported
initialize();

// Export for testing and explicit initialization
export default { initialize };